import type { Atom } from "@pdfking/tfunc"
import type { IterableLike } from "../types/IterableLike"
import { map } from "./map"
import { ordered_object } from "./ordered_object"

/**
 * Maps iterable to object with custom key and value functions.
 *
 * @param keyf - Function to generate keys from (value, key/index)
 * @param valuef - Function to generate values from (value, key/index)
 * @param iterable - Array, object, or primitive to map
 * @returns Object with mapped keys and values
 *
 * @example
 * ```ts
 * import { object_map } from "@pdfking/rfunc"
 *
 * object_map(v => v, v => v + 2, [1, 2, 3]) // { 1: 3, 2: 4, 3: 5 }
 * object_map((v, i) => i + 1, (v, i) => v + 2, [4, 5, 6]) // { 1: 6, 2: 7, 3: 8 }
 * ```
 */
export function object_map<
  T extends Atom | null | undefined,
  K extends PropertyKey,
  V,
>(
  keyf: (value: T, key: number) => K,
  valuef: (value: T, key: number) => V,
  iterable: T
): Record<K, V>
export function object_map<T, K extends PropertyKey, V>(
  keyf: (value: T, key: number) => K,
  valuef: (value: T, key: number) => V,
  iterable: T[]
): Record<K, V>
export function object_map<T, K extends PropertyKey, V>(
  keyf: (value: T, key: number) => K,
  valuef: (value: T, key: number) => V,
  iterable: Iterable<T>
): Record<K, V>
export function object_map<S extends string, T, K extends PropertyKey, V>(
  keyf: (value: T, key: S) => K,
  valuef: (value: T, key: S) => V,
  iterable: Record<S, T>
): Record<K, V>
export function object_map<T, K extends PropertyKey, V>(
  keyf: (value: T, key: any) => K,
  valuef: (value: T, key: any) => V,
  iterable: IterableLike<T>
): Record<K, V> {
  return ordered_object(
    map(
      (value: T, key) => [keyf(value, key), valuef(value, key)] as [K, V],
      iterable as T[]
    )
  )
}
